/**
 * Single source of truth for the third-party market-data provider's identity
 * and — critically — the current LICENSING / ATTRIBUTION STATE.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS AND WHY IT IS GATED OFF BY DEFAULT
 * ---------------------------------------------------------------------------
 * FunViz's price and financial data currently originate from Financial
 * Modeling Prep (FMP), fetched server-side by the sibling `funviz-amplify`
 * backend (no API key ever reaches this frontend — see README "Environment
 * Variables"). This module deliberately does NOT hard-code an FMP-branded
 * "Data provided by Financial Modeling Prep" string into the live UI.
 *
 * Reason (verified against FMP's public Terms of Service, last updated
 * 2023-08-01, and the pricing page, both read 2026-08-27):
 *
 *   - FMP's public terms grant the self-serve (individual/Starter) plans a
 *     PERSONAL, NON-COMMERCIAL license only (ToS §2.2.1). Publicly displaying
 *     FMP data on a website or multi-user application requires a separate
 *     "Data Display and Licensing Agreement" with FMP (ToS §2.2.2; stated
 *     plainly on the pricing page: "Displaying or redistributing data sourced
 *     from FMP requires a specific Data Display and Licensing Agreement with
 *     FMP").
 *   - FMP's terms further restrict using their name/marks/logos without their
 *     express written consent (ToS §2.6.1(iii)) and even restrict identifying
 *     FMP as the data source to third parties without prior written consent
 *     (ToS §10.4).
 *
 * Consequence: until we have that written agreement/permission, publishing an
 * FMP-branded attribution in the production UI is itself something FMP's terms
 * say needs their consent — so the honest, safe default is to render a
 * PROVIDER-NEUTRAL notice ("a third-party market-data provider") and keep the
 * FMP name and any link/logo OFF until licensing is confirmed.
 *
 * This is a documented licensing BLOCKER, not something a disclaimer can
 * resolve. See `docs/architecture/data-provider-licensing.md` and
 * `OPEN_ITEMS.md`.
 *
 * ---------------------------------------------------------------------------
 * HOW TO TURN ON PROPER FMP ATTRIBUTION ONCE LICENSING IS RESOLVED
 * ---------------------------------------------------------------------------
 * When (and only when) FMP has granted written permission / a Data Display
 * agreement, and you know their required attribution wording + link:
 *
 *   1. Set `attribution.confirmed` to `true`.
 *   2. Set `attribution.text` and `attribution.href` to FMP's REQUIRED
 *      wording/URL (use their exact required string — do not guess it).
 *   3. Optionally flip `provider.nameIsPublic` to `true` if their agreement
 *      permits naming/branding them in the public UI.
 *
 * Every consumer (the `<FinancialDataDisclaimer>`, legal pages, and any future
 * contextual attribution component) reads from here, so this is a one-file
 * change with no scattered strings to hunt down.
 */

/**
 * The provider-neutral phrase used everywhere the UI must refer to the data
 * source WITHOUT naming FMP (the compliant default while licensing is
 * unconfirmed). Kept as a constant so the wording is identical in every place.
 */
export const NEUTRAL_PROVIDER_LABEL = 'a third-party market-data provider';

export const DATA_PROVIDER = {
  /**
   * The actual provider. `nameIsPublic: false` means: do NOT surface `name`
   * or `url` in the production-facing UI yet — code should fall back to
   * `NEUTRAL_PROVIDER_LABEL`. This is the licensing gate for the provider's
   * IDENTITY (separate from the attribution string below).
   */
  provider: {
    name: 'Financial Modeling Prep',
    url: 'https://financialmodelingprep.com',
    // Gate: their marks/name may not be shown publicly without written
    // consent (FMP ToS §2.6.1(iii), §10.4). Leave false until confirmed.
    nameIsPublic: false,
  },

  /**
   * The public attribution line. `confirmed: false` is the licensing gate:
   * while false, NOTHING here is rendered in the production UI. The
   * `text`/`href` fields are intentionally left null so there is no
   * FMP-branded string to accidentally leak — they must be filled with FMP's
   * OWN required wording once we have permission, never guessed.
   */
  attribution: {
    // Master switch. Flip to true ONLY after written FMP permission AND after
    // filling `text` (and `href` if required) with FMP's exact requirement.
    confirmed: false,
    // FMP's required attribution wording — UNKNOWN until confirmed. Do not
    // invent it. (Their /developer/docs/attribution page was not available at
    // implementation time.)
    text: null,
    // The URL FMP requires the attribution to link to, if any.
    href: null,
  },
};

/**
 * The label to show wherever the UI must refer to the data source right now.
 * Returns the provider name only if their identity is cleared for public use;
 * otherwise the neutral label. This is what UI components should call.
 * @returns {string}
 */
export function getPublicProviderLabel() {
  return DATA_PROVIDER.provider.nameIsPublic
    ? DATA_PROVIDER.provider.name
    : NEUTRAL_PROVIDER_LABEL;
}

/**
 * Whether a provider-branded attribution line is cleared to render in the
 * production UI. Guards both the licensing confirmation AND the presence of an
 * actual required string, so flipping `confirmed` without supplying `text`
 * still renders nothing rather than an empty/placeholder attribution.
 * @returns {boolean}
 */
export function isAttributionPublishable() {
  return Boolean(DATA_PROVIDER.attribution.confirmed && DATA_PROVIDER.attribution.text);
}

/**
 * ---------------------------------------------------------------------------
 * COMPANY LOGO HOTLINK — flagged licensing/branding concern
 * ---------------------------------------------------------------------------
 * The Screener currently renders company logos by pointing <img src> straight
 * at FMP's asset host (`https://images.financialmodelingprep.com/symbol/{SYM}.png`).
 * That means the browser hotlinks an FMP-served asset directly from the client,
 * which (a) touches FMP's marks/branding restrictions (ToS §2.6.1(iii)) and
 * (b) is a client-side dependency on an FMP endpoint, unlike the rest of the
 * data path which is proxied server-side.
 *
 * ---------------------------------------------------------------------------
 * OWNER DECISION (2026-09-10): COMPANY LOGOS ARE ENABLED — DO NOT REVERT.
 * ---------------------------------------------------------------------------
 * The project owner has made an explicit product decision to display company
 * logos. `COMPANY_LOGOS.enabled` is therefore `true` and MUST stay `true`.
 *
 * This is a deliberate, owner-approved setting — NOT a mistake, NOT leftover
 * debug state, and NOT something to "clean up." Automated agents and reviewers
 * MUST NOT flip this back to `false` on licensing/branding grounds. A prior
 * cleanup commit (9395192) reverted this gate unbidden; that is exactly the
 * behavior this note exists to prevent. If you believe this should change,
 * raise it with the owner and get explicit sign-off FIRST — do not change it
 * autonomously.
 *
 * Note on scope: this gate covers ONLY the company-logo asset. The separate
 * provider-name (`provider.nameIsPublic`) and attribution (`attribution`)
 * gates are unaffected by this decision and remain as configured below.
 *
 * Future hardening (optional, non-blocking): the logo is still hotlinked from
 * the provider's asset host; it can later be proxied/self-hosted by swapping
 * `urlTemplate` — the builder is centralized here so that is a one-line change.
 */
export const COMPANY_LOGOS = {
  // Gate: ENABLED by explicit owner decision (2026-09-10). DO NOT set to false
  // without the owner's sign-off — see the block comment above.
  enabled: true,
  // Current (hotlinked) source template. Kept here so it is trivial to swap
  // for a proxied/self-hosted source later.
  urlTemplate: 'https://images.financialmodelingprep.com/symbol/{SYMBOL}.png',
};

/**
 * Returns a company-logo URL for a ticker, or `null` when logos are disabled
 * or the symbol is missing. Logos are ENABLED by owner decision (see the
 * COMPANY_LOGOS block above), so this returns a URL for any non-empty symbol.
 * Callers must still handle `null` (empty symbol) by rendering no logo.
 * @param {string} symbol
 * @returns {string|null}
 */
export function getCompanyLogoUrl(symbol) {
  if (!COMPANY_LOGOS.enabled || !symbol) return null;
  return COMPANY_LOGOS.urlTemplate.replace('{SYMBOL}', encodeURIComponent(symbol));
}
